import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BadgeCheck, ArrowUpRight } from 'lucide-react';
import { HERO_IMAGES, BRAND, cleanImageUrl } from '../../lib/constants';
import { fetchArtists } from '../../lib/api';
import { useRegistrationUrl } from '../../lib/useSettings';
import { trackEvent } from '../../lib/analytics';
import { ikHero, ikThumb } from '../../lib/imagekit';

// Build the rotating slide list:
// - Each slide is one featured artist (uses their featured_image → cover_image → profile_image)
// - If no featured artists exist, fall back to the static HERO_IMAGES so the hero never looks empty
const buildSlides = (featuredArtists) => {
  if (featuredArtists.length > 0) {
    return featuredArtists.map(a => ({
      image: cleanImageUrl(a.featured_image) || a.cover_image || a.profile_image,
      artist: a,
    }));
  }
  return HERO_IMAGES.map(src => ({ image: src, artist: null }));
};

export const Hero = () => {
  const [idx, setIdx] = useState(0);
  const [slides, setSlides] = useState(() => buildSlides([]));
  const registrationUrl = useRegistrationUrl();

  useEffect(() => {
    // Pull only featured + published artists (publish filter handled server-side)
    fetchArtists({ featured: true, limit: 10 })
      .then(arr => {
        if (Array.isArray(arr) && arr.length > 0) {
          setSlides(buildSlides(arr));
          setIdx(0);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (slides.length <= 1) return;
    const t = setInterval(() => setIdx(i => (i + 1) % slides.length), 5500);
    return () => clearInterval(t);
  }, [slides.length]);

  const current = slides[idx] || slides[0];
  const featured = current?.artist || null;

  return (
    <section className="relative w-full min-h-[100vh] bg-white" data-testid="hero-section">
      <div className="relative grid lg:grid-cols-12 min-h-[100vh]">
        {/* Left: copy */}
        <div className="lg:col-span-6 flex items-end lg:items-center pt-32 lg:pt-0 pb-12 lg:pb-0 container-edge">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-[#eb5381]">
              <span className="w-8 h-px bg-[#eb5381]" />
              India's Verified Beauty Marketplace
            </div>
            <h1 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-balance">
              Find Your <span className="italic text-[#eb5381]">Dream</span><br />
              Makeup Artist.
            </h1>
            <p className="mt-7 text-lg text-neutral-700 max-w-md leading-relaxed">
              Verified makeup artists. Real portfolios. Seamless bookings — for the brides who believe their best day deserves the best brush.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link
                to="/artists"
                data-testid="hero-find-mua-btn"
                className="inline-flex items-center justify-center gap-3 bg-[#eb5381] hover:bg-[#d64570] text-white px-9 py-4 text-[11px] uppercase tracking-[0.25em] transition-colors"
              >
                Find Your MUA
                <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="/register"
                data-testid="hero-register-mua-btn"
                className="inline-flex items-center justify-center gap-3 border border-black hover:bg-black hover:text-white px-9 py-4 text-[11px] uppercase tracking-[0.25em] transition-colors"
              >
                Register as MUA
              </a>
            </div>

            <div className="mt-14 grid grid-cols-3 gap-6 max-w-md" data-testid="hero-stats">
              <div>
                <div className="font-serif text-3xl">100<span className="text-[#eb5381]">%</span></div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-1">Verified Artists</div>
              </div>
              <div>
                <div className="font-serif text-3xl">Curated</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-1">Handpicked MUAs</div>
              </div>
              <div>
                <div className="font-serif text-3xl">Easy</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-1">Stress-Free Booking</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: rotating featured-artist hero images */}
        <div className="lg:col-span-6 relative overflow-hidden bg-[#ffefd1] min-h-[60vh] lg:min-h-screen">
          {slides.map((s, i) => (
            <div
              key={`${s.artist?.slug || i}-${s.image}`}
              className={`absolute inset-0 transition-opacity duration-[1400ms] ease-out ${
                i === idx ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={ikHero(s.image)}
                alt={s.artist ? `${s.artist.name} — ${s.artist.city}` : 'Indian bridal makeup'}
                className={`w-full h-full object-cover ${i === idx ? 'animate-slow-zoom' : ''}`}
              />
            </div>
          ))}

          {/* Featured Artist card — synced with the current background slide */}
          {featured && (
            <Link
              to={`/artists/${featured.slug}`}
              data-testid="hero-featured-artist"
              key={featured.slug}
              className="absolute bottom-32 right-6 md:right-8 z-10 hidden md:block group"
            >
              <div className="bg-white/95 backdrop-blur-md px-5 py-4 w-[260px] transition-transform group-hover:-translate-y-1">
                <div className="flex items-center justify-between">
                  <div className="text-[9px] uppercase tracking-[0.3em] text-[#eb5381] inline-flex items-center gap-1.5">
                    <BadgeCheck className="w-3.5 h-3.5" />
                    Featured Artist
                  </div>
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </div>
                <div className="mt-3 flex items-center gap-3">
                  <div className="w-12 h-12 overflow-hidden shrink-0 bg-[#f7f7f7]">
                    <img src={ikThumb(featured.profile_image, 200)} alt={featured.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-serif text-lg leading-tight truncate">{featured.name}</div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 mt-0.5 truncate">
                      {featured.city}
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          )}

          {/* Now booking + dots */}
          <div className="absolute bottom-8 left-8 right-8 z-10 flex items-end justify-between">
            <div className="bg-black/50 backdrop-blur-md text-white px-5 py-3 max-w-xs">
              <div className="text-[10px] uppercase tracking-[0.3em] opacity-80">Now Booking</div>
              <div className="font-serif text-xl leading-tight mt-1">2026 &amp; 2027 Wedding Season</div>
            </div>
            <div className="flex gap-2">
              {slides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  className={`h-1 transition-all ${i === idx ? 'w-10 bg-white' : 'w-5 bg-white/50'}`}
                  aria-label={`Slide ${i + 1}`}
                  data-testid={`hero-slide-dot-${i}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Marquee strip */}
      <div className="border-t border-b border-[#eaeaea] bg-white py-4 md:py-5 overflow-hidden">
        <div className="flex gap-8 md:gap-16 marquee-track whitespace-nowrap">
          {[
            'Verified Portfolios',
            'Real Reviews',
            'Trial Sessions',
            'Home & Studio Service',
            'HD · Airbrush · Soft Glam',
            'Pan-India Coverage',
            'Curated By Editors',
            'Verified Portfolios',
            'Real Reviews',
            'Trial Sessions',
            'Home & Studio Service',
            'HD · Airbrush · Soft Glam',
            'Pan-India Coverage',
            'Curated By Editors',
          ].map((t, i) => (
            <span key={i} className="text-[10px] md:text-[12px] uppercase tracking-[0.2em] md:tracking-[0.35em] text-neutral-500 inline-flex items-center gap-8 md:gap-16 shrink-0">
              {t}
              <span className="text-[#eb5381]">✦</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};
